/* =========================================================
   Caballero Armado — Theme JS
   Header, mobile menu, reveal animations, video scrubbing.
   Vanilla JS, no dependencies.
   ========================================================= */
(function () {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ---------- Header scroll state ---------- */
  function initHeader() {
    const header = $('.ca-header');
    if (!header) return;
    const isTransparent = header.classList.contains('ca-header--transparent');
    const threshold = 40;

    const update = () => {
      if (window.scrollY > threshold) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
    };
    update();
    window.addEventListener('scroll', update, { passive: true });

    if (!isTransparent) header.classList.add('is-solid');
  }

  /* ---------- Mobile menu ---------- */
  function initMobileMenu() {
    const header = $('.ca-header');
    const burger = $('.ca-header__burger');
    const menu = $('.ca-mobile-menu');
    if (!burger || !menu || !header) return;

    const toggle = (open) => {
      const isOpen = open ?? !menu.classList.contains('is-open');
      menu.classList.toggle('is-open', isOpen);
      header.classList.toggle('is-menu-open', isOpen);
      document.documentElement.style.overflow = isOpen ? 'hidden' : '';
      burger.setAttribute('aria-expanded', String(isOpen));
    };

    burger.addEventListener('click', () => toggle());
    $$('.ca-mobile-menu a').forEach(a => a.addEventListener('click', () => toggle(false)));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') toggle(false); });
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal() {
    const items = $$('.ca-reveal');
    if (!items.length || !('IntersectionObserver' in window)) {
      items.forEach(el => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    items.forEach(el => io.observe(el));
  }

  /* ---------- Video scrubbing — el scroll controla el video y las etapas ---------- */
  function initVideoScrub() {
    const wrapper = $('[data-ca-hero-scroll]');
    if (!wrapper) return;

    const video      = wrapper.querySelector('[data-ca-hero-video]');
    const content    = wrapper.querySelector('[data-ca-hero-content]');
    const scrollHint = wrapper.querySelector('[data-ca-hero-hint]');
    const progressBar = wrapper.querySelector('[data-ca-progress]');
    const vignette   = wrapper.querySelector('[data-ca-hero-vignette]');
    const stages     = Array.from(wrapper.querySelectorAll('[data-ca-stage]')).map((el) => ({
      el,
      start: parseFloat(el.getAttribute('data-start')) || 0,
      end: parseFloat(el.getAttribute('data-end')) || 1
    }));

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = window.innerWidth < 768;
    const canScrub = video && !prefersReduced && !isMobile;

    if (video && !canScrub) {
      /* Móvil o reduce-motion: autoplay en loop, sin etapas */
      video.setAttribute('autoplay', '');
      video.setAttribute('loop', '');
      video.muted = true;
      video.play().catch(() => {});
      return;
    }

    if (video) {
      video.preload = 'auto';
      video.pause();
      video.addEventListener('loadeddata', () => {
        if (video.currentTime === 0) video.currentTime = 0.001;
      });
    }

    let target = 0;     /* progreso real según el scroll */
    let smooth = 0;     /* progreso suavizado que mueve el video */
    let animating = false;
    let lastFrameTime = -1;
    const FPS = 30;     /* cuantizar seeks a frames reales del video */

    const computeProgress = () => {
      const rect = wrapper.getBoundingClientRect();
      const total = Math.max(rect.height - window.innerHeight, 1);
      target = Math.min(Math.max(-rect.top / total, 0), 1);
    };

    const apply = (p) => {
      /* Avance del video: solo buscar cuando cambia el frame (evita seeks redundantes) */
      if (video && video.readyState >= 1 && video.duration) {
        const t = Math.round(p * video.duration * FPS) / FPS;
        if (Math.abs(t - lastFrameTime) > 0.001) {
          lastFrameTime = t;
          try { video.currentTime = t; } catch (e) {}
        }
      }

      /* Contenido inicial: se desvanece en el primer 18% */
      const fade = Math.min(p / 0.18, 1);
      if (content) {
        content.style.opacity = String(1 - fade);
        content.style.transform = 'translate3d(0, ' + (fade * -50) + 'px, 0)';
        content.style.pointerEvents = fade >= 1 ? 'none' : '';
      }
      if (scrollHint) scrollHint.style.opacity = String(Math.max(1 - p * 5, 0));
      if (progressBar) progressBar.style.width = (target * 100) + '%';
      if (vignette) vignette.style.opacity = String(0.45 + p * 0.35);

      /* Etapas: cada una entra y sale en su rango del recorrido */
      for (let i = 0; i < stages.length; i++) {
        const s = stages[i];
        const span = s.end - s.start;
        const fz = Math.max(Math.min(span * 0.3, 0.07), 0.02); /* zona de fade */
        let o = 0;
        if (p >= s.start && p <= s.end) {
          o = Math.max(0, Math.min((p - s.start) / fz, (s.end - p) / fz, 1));
        } else if (i === stages.length - 1 && p > s.end) {
          /* la última etapa se mantiene visible hasta el final */
          o = 1;
        }
        s.el.style.opacity = String(o);
        s.el.style.transform = 'translate3d(0, ' + ((1 - o) * 26) + 'px, 0)';
        s.el.classList.toggle('is-active', o > 0.5);
      }
    };

    const tick = () => {
      smooth += (target - smooth) * 0.18;
      if (Math.abs(target - smooth) < 0.0005) smooth = target;
      apply(smooth);
      if (smooth !== target) {
        requestAnimationFrame(tick);
      } else {
        animating = false;
      }
    };

    const onScroll = () => {
      computeProgress();
      if (!animating) {
        animating = true;
        requestAnimationFrame(tick);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    if (video) video.addEventListener('loadedmetadata', onScroll);
    onScroll();
  }

  /* ---------- Cart count ---------- */
  function initCartCount() {
    const node = $('[data-ca-cart-count]');
    if (!node || !window.fetch) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(r => r.ok ? r.json() : null)
      .then(cart => {
        if (!cart) return;
        const count = cart.item_count || 0;
        node.textContent = count;
        node.style.display = count > 0 ? 'inline-flex' : 'none';
      })
      .catch(() => {});
  }

  /* ---------- Boot ---------- */
  const ready = (fn) => (document.readyState !== 'loading')
    ? fn()
    : document.addEventListener('DOMContentLoaded', fn);

  ready(() => {
    initHeader();
    initMobileMenu();
    initReveal();
    initVideoScrub();
    initCartCount();
  });
})();
